import React from 'react';
import { cn } from '@/lib/utils';
import { useResponsive } from '@/hooks/useResponsive';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ResponsiveTableColumn<T> {
  key: keyof T | string;
  header: string;
  cell: (item: T) => React.ReactNode;
  className?: string;
  hideOnMobile?: boolean;
  priority?: 'high' | 'medium' | 'low'; // For showing in mobile card view
}

interface ResponsiveTableProps<T> {
  data: T[];
  columns: ResponsiveTableColumn<T>[];
  className?: string;
  cardTitle?: (item: T) => string;
  cardSubtitle?: (item: T) => string;
  cardActions?: (item: T) => React.ReactNode;
  onRowClick?: (item: T) => void;
  loading?: boolean;
  emptyMessage?: string;
}

/**
 * ResponsiveTable - Mobile-first table component
 * 
 * Automatically switches between table view (desktop) and card view (mobile)
 * based on screen size. Provides excellent UX across all device sizes.
 */
export function ResponsiveTable<T extends Record<string, any>>({
  data,
  columns,
  className,
  cardTitle,
  cardSubtitle,
  cardActions,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available'
}: ResponsiveTableProps<T>) {
  const { isMobile } = useResponsive();

  if (loading) {
    return (
      <div className={cn('space-y-4', className)}>
        {isMobile ? (
          // Mobile loading skeleton - cards
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))
        ) : (
          // Desktop loading skeleton - table
          <div className="rounded-md border">
            <div className="p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('text-center py-8 text-muted-foreground', className)}>
        {emptyMessage}
      </div>
    );
  }

  if (isMobile) {
    // Mobile card view
    return (
      <div className={cn('space-y-4', className)}>
        {data.map((item, index) => (
          <Card 
            key={index} 
            className={cn(
              'transition-shadow hover:shadow-md',
              onRowClick && 'cursor-pointer hover:bg-gray-50'
            )}
            onClick={() => onRowClick?.(item)}
          >
            <CardHeader className="pb-3">
              {cardTitle && (
                <CardTitle className="text-base font-medium">
                  {cardTitle(item)}
                </CardTitle>
              )}
              {cardSubtitle && (
                <p className="text-sm text-muted-foreground">
                  {cardSubtitle(item)}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Show high priority fields first */}
              {columns
                .filter(col => col.priority === 'high' || !col.hideOnMobile)
                .map((column, colIndex) => (
                  <div key={colIndex} className="flex justify-between items-center">
                    <span className="text-sm font-medium text-muted-foreground">
                      {column.header}
                    </span>
                    <div className="text-sm">
                      {column.cell(item)}
                    </div>
                  </div>
                ))}
              
              {/* Actions */}
              {cardActions && (
                <div className="pt-2 border-t">
                  {cardActions(item)}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className={cn('rounded-md border overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={cn(
                    'h-12 px-4 text-left align-middle font-medium text-muted-foreground',
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  'border-b transition-colors hover:bg-muted/50',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={cn(
                      'p-4 align-middle',
                      column.className
                    )}
                  >
                    {column.cell(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Utility components for common table cell types
 */
export const TableCellBadge: React.FC<{ 
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  children: React.ReactNode;
}> = ({ variant = 'default', children }) => (
  <Badge variant={variant} className="whitespace-nowrap">
    {children}
  </Badge>
);

export const TableCellActions: React.FC<{ 
  children: React.ReactNode;
}> = ({ children }) => (
  <div className="flex items-center gap-2">
    {children}
  </div>
);

export const TableCellText: React.FC<{ 
  primary: string;
  secondary?: string;
}> = ({ primary, secondary }) => (
  <div>
    <div className="font-medium">{primary}</div>
    {secondary && (
      <div className="text-sm text-muted-foreground">{secondary}</div>
    )}
  </div>
);

export default ResponsiveTable;